import { PayloadAction } from '@reduxjs/toolkit';
import moment from 'moment';
import { createIntl } from 'react-intl';
import { put, select, takeLatest, delay } from 'redux-saga/effects';
import { toastService } from 'components/molecules/toast';
import { formatDate } from 'components/organisms/projects/project-plan/timeline/helpers';
import { CreateDependencyActionPayload } from 'data/tasks/actions';
import { logger } from 'services';
import translations from 'translations/en.json';
import { addDays, EntityType, getDateShiftsForEntity } from 'utils/date-shift';
import {
  getPhaseList,
  getTaskList,
  getTemplatePhaseUpdates,
  getTemplateTaskUpdates,
  isValidDateShift,
} from 'utils/template-date-shift';
import {
  addTaskDependencyInProjectTemplate,
  UpdateTaskInProjectTemplate,
  updateTaskInProjectTemplate,
} from '../actions';
import {
  selectAllTemplateProjectPhase,
  selectAllTemplateTask,
} from '../selectors';
import {
  makeDateShiftsForProjectPhases,
  makeDateShiftsForTasks,
  updateProjectTemplateTask,
} from '../slices';

const intl = createIntl({
  locale: 'en',
  messages: translations,
});

function* onUpdateTask({
  payload,
}: PayloadAction<UpdateTaskInProjectTemplate>): Generator {
  try {
    const { taskId, payload: data } = payload;
    const { startDay, duration } = data;

    if (!startDay && !duration) {
      return;
    }

    const phases: any = yield select(selectAllTemplateProjectPhase());
    const tasks: any = yield select(selectAllTemplateTask());

    const taskList = getTaskList(Object.values(tasks));
    const entity = taskList[taskId];

    if (!entity) {
      logger.warn('Task not found during date shift on task update', {
        taskId,
      });

      return;
    }

    const newStartDate = startDay
      ? formatDate(moment().add(startDay, 'day'))
      : entity.startDate;
    const newDueDate = duration
      ? formatDate(addDays(newStartDate, duration, true))
      : entity.dueDate;

    const [phaseUpdates, taskUpdates] = getDateShiftsForEntity(
      getPhaseList(Object.values(phases)),
      taskList,
      {
        id: taskId,
        type: EntityType.Task,
        depth: taskList[taskId].depth,
        ...(startDay && { startDate: newStartDate }),
        ...(duration && { dueDate: newDueDate }),
      },
      {
        allowWeekend: true,
        alignParentWithChildren: true,
      },
    );

    const isValid = isValidDateShift(phaseUpdates);

    if (!isValid) {
      yield put(
        updateProjectTemplateTask({
          task: {
            taskId,
            startDay: tasks[taskId]?.startDay,
          },
        }),
      );
      toastService({
        title: intl.formatMessage({ id: 'failure' }),
        message: intl.formatMessage({
          id: 'templates.date.shift.start.day.error',
        }),
      }).error();

      return;
    }

    /**
     * added delay of 1ms to nullify date shift changes from other actions
     * TODO: to refactor unnecessary actions/ handlers.
     */
    yield delay(1);
    yield put(
      makeDateShiftsForProjectPhases(getTemplatePhaseUpdates(phaseUpdates)),
    );
    yield put(
      makeDateShiftsForTasks(getTemplateTaskUpdates(taskUpdates, taskList)),
    );
  } catch (e) {
    logger.error('Exception on date shift on template task update', {
      error: e,
    });
  }
}

function* onDependencyCreated({
  payload,
}: PayloadAction<CreateDependencyActionPayload>): Generator {
  try {
    const { taskId, dependentTaskId } = payload;
    const phases: any = yield select(selectAllTemplateProjectPhase());
    const tasks: any = yield select(selectAllTemplateTask());

    const taskList = getTaskList(Object.values(tasks));
    const [phaseUpdates, taskUpdates] = getDateShiftsForEntity(
      getPhaseList(Object.values(phases)),
      taskList,
      {
        id: dependentTaskId,
        dependencyFor: [
          ...(taskList?.[dependentTaskId]?.dependencyFor || []),
          taskId,
        ],
        type: EntityType.Task,
        depth: taskList[dependentTaskId].depth,
      },
      {
        allowWeekend: true,
        alignParentWithChildren: true,
      },
    );

    yield put(
      makeDateShiftsForProjectPhases(getTemplatePhaseUpdates(phaseUpdates)),
    );
    yield put(
      makeDateShiftsForTasks(getTemplateTaskUpdates(taskUpdates, taskList)),
    );
  } catch (e) {
    logger.error('Exception on date shift on dependency create', { error: e });
  }
}

export const watchTemplateDateShiftSaga = function* (): Generator {
  yield takeLatest(updateTaskInProjectTemplate.type, onUpdateTask);
  yield takeLatest(
    addTaskDependencyInProjectTemplate.type,
    onDependencyCreated,
  );
};
